import {
  TableCellProps,
  WbButton,
  WbMoreOptions,
  WbTable,
  WbTableFilters,
} from '@agilelab/plugin-wb-platform';
import { RoleEntity } from '@agilelab/plugin-wb-rbac-common';
import { ErrorPanel, Progress } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  Box,
  ListItemIcon,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleDetailRouteRef } from '../../../routes';
import { NewEditRoleDrawer } from '../RoleDrawer';
import { RoleFormType } from '../RoleForm';
import { useRolesTableContext } from './RolesTableContext';

const useStyles = makeStyles(
  theme => ({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  }),
  { name: 'RolesTable' },
);

interface RolesTableProps {}

const RolesActions: FC = () => {
  const {
    selectedDeleteItems,
    openDrawer,
    setOpenDrawer,
    error,
    canDeleteNewRolePermission,
    canAddNewRolePermission,
    setOpenConfirmDialog,
    addRole,
    addRoleState,
  } = useRolesTableContext();

  const onSubmit = useCallback(
    (newValues: RoleFormType) => {
      addRole(newValues);
    },
    [addRole],
  );
  return (
    <>
      <Box display="flex" style={{ gap: '8px' }} height="fit-content">
        {!!selectedDeleteItems?.length
          ? canDeleteNewRolePermission && (
              <>
                <WbButton
                  label="Delete selected"
                  startIcon={<DeleteIcon />}
                  onClick={() => setOpenConfirmDialog(true)}
                  color="secondary"
                  size="small"
                  variant="outlined"
                />
              </>
            )
          : canAddNewRolePermission && (
              <WbButton
                label="Add role"
                startIcon={<AddCircleIcon />}
                onClick={() => setOpenDrawer(true)}
                color="primary"
                size="small"
              />
            )}
      </Box>
      <NewEditRoleDrawer
        onSubmit={onSubmit}
        dataState={addRoleState}
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        error={error}
      />
    </>
  );
};

export const RolesCardHeader: FC = () => {
  const classes = useStyles();
  const {
    selectedDeleteItems,
    setSelectedDeleteItems,
    filters,
    setFilters,
    setPagination,
  } = useRolesTableContext();

  return (
    <Box className={classes.header}>
      <Box className={classes.flexBox}>
        <WbTableFilters
          style={{ marginBottom: '0' }}
          searchValue={filters.text}
          onSearch={search => {
            setFilters({ text: search });
            setPagination(p => ({ ...p, offset: 0 }));
          }}
          onClear={() => setFilters({})}
        />
        {selectedDeleteItems && selectedDeleteItems?.length > 0 && (
          <Box className={classes.flexBox}>
            <Typography variant="body2" style={{ fontWeight: 'medium' }}>
              {selectedDeleteItems.length} Roles selected
            </Typography>
            <WbButton
              variant="text"
              label="Clear selection"
              onClick={() => setSelectedDeleteItems([])}
            />
          </Box>
        )}
      </Box>
      <RolesActions />
    </Box>
  );
};

const Actions = ({ row }: { row: RoleEntity }) => {
  const {
    setSelectedRow,
    selectedRow,
    setOpenConfirmDialog,
    canDeleteNewRolePermission,
    checkIfDisabled,
  } = useRolesTableContext();

  const navigate = useNavigate();
  const roleDetailPageRef = useRouteRef(roleDetailRouteRef);

  const onMenuClose = () => {
    setSelectedRow(undefined);
  };

  const items = useMemo(() => {
    const defaultItems = [
      <MenuItem
        key={`${row.id}-manage`}
        onClick={() => {
          navigate({
            pathname: `${roleDetailPageRef({ roleId: row.id })}/assignments`,
          });
        }}
      >
        <ListItemIcon>
          <AccountBoxIcon fontSize="small" />
        </ListItemIcon>
        Add Assignments
      </MenuItem>,
    ];

    if (canDeleteNewRolePermission && !checkIfDisabled(row)) {
      defaultItems.push(
        <MenuItem
          key={`${row.id}-delete`}
          onClick={() => {
            setOpenConfirmDialog(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Role
        </MenuItem>,
      );
    }
    return defaultItems;
  }, [
    canDeleteNewRolePermission,
    checkIfDisabled,
    navigate,
    roleDetailPageRef,
    row,
    setOpenConfirmDialog,
  ]);

  return !!items?.length ? (
    <WbMoreOptions
      key={row.id}
      open={selectedRow?.id === row.id}
      IconProps={{ style: { fontSize: 16 } }}
      MenuProps={{ onClick: e => e.stopPropagation() }}
      onClick={e => {
        e.stopPropagation();
        setSelectedRow(row);
      }}
      onClose={onMenuClose}
    >
      {items}
    </WbMoreOptions>
  ) : (
    <></>
  );
};

export const RolesTable: FC<RolesTableProps> = () => {
  const {
    selectedDeleteItems,
    setSelectedDeleteItems,
    rolesLoaded,
    pagination,
    setPagination,
    canDeleteNewRolePermission,
    checkIfDisabled,
  } = useRolesTableContext();

  const navigate = useNavigate();
  const roleDetailPageRef = useRouteRef(roleDetailRouteRef);

  const columns: TableCellProps<RoleEntity>[] = [
    {
      field: 'id',
      cellProps: {
        size: 'small',
        align: 'left',
        style: { wordBreak: 'break-word' },
      },
      headerName: 'ID',
    },
    {
      field: 'displayName',
      cellProps: {
        size: 'small',
        align: 'left',
        style: { wordBreak: 'break-word' },
      },
      headerName: 'Name',
    },
    {
      field: 'description',
      tooltip: true,
      cellProps: {
        size: 'small',
        align: 'left',
        style: { wordBreak: 'break-word' },
      },
      headerName: 'Description',
    },
    {
      field: 'actions',
      fieldRender: row => <Actions row={row} />,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
    },
  ];

  if (rolesLoaded.error)
    return (
      <ErrorPanel
        error={
          rolesLoaded.error instanceof Error
            ? rolesLoaded.error
            : new Error('Something went wrong')
        }
      />
    );

  const onClickHandle = (
    row: RoleEntity,
    event: MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => {
    event.preventDefault();

    event.stopPropagation();
    navigate({ pathname: roleDetailPageRef({ roleId: row.id }) });
  };

  const getRowId = (r: RoleEntity) => r.id;

  return (
    <WbTable<RoleEntity>
      selection={canDeleteNewRolePermission ? 'multiple' : undefined}
      selectedItems={selectedDeleteItems}
      setSelectedItems={setSelectedDeleteItems}
      checkIfDisabled={checkIfDisabled}
      tooltipIfDisabled="This is a platform permission. You cannot delete"
      styles={{
        container: { height: '100%', flex: '1 1 0' },
      }}
      components={{
        tableLoader: {
          loading: rolesLoaded.loading,
          loader: <Progress />,
        },
        tableContent: {
          columns,
          getRowId,
          rows: rolesLoaded.value?.roles,
          onRowClick: onClickHandle,
        },
      }}
      pagination={{
        rowsPerPageOptions: [25, 50, 100],
        count: rolesLoaded.value?.total || 0,
        limit: pagination.limit,
        offset: pagination.offset,
        onPageChange: (page: number) => {
          setPagination({
            ...pagination,
            offset: page * pagination.limit,
          });
        },
        onRowsPerPageChange: (rowsPerPage: number) => {
          setPagination({
            offset: 0,
            limit: rowsPerPage,
          });
        },
      }}
    />
  );
};
